import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, CardHeader, Row, Col, Input } from 'reactstrap';
import SnackbarContent from '@mui/material/SnackbarContent';
import LinearProgress from '@mui/material/LinearProgress';
import { IconButton, Tooltip, MenuItem, Select, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import OrderFailedReport from './OrderFailedReport';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';



const inputStyle = { borderColor: '#fca5a5' };

const OrderFailedReportIndex = () => {
    const [loading, setLoading] = useState(false);
    const [isNoRecord, setNoRecord] = useState(false);
    const [isReport, setReport] = useState(null);
    const [exportReport, setExportReport] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [orderNo, setOrderNo] = useState('');
    const [hubId, setHubId] = useState('');          // selected hub
    const [hubOptions, setHubOptions] = useState([]);          // [{label, value}]
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalElements, setTotalElements] = useState(0);

    useEffect(() => {
        loadHubs();
    }, []);

    // ─── Load hub dropdown ────────────────────────────────────────────────────
    const loadHubs = async () => {
        try {
            const res = await PndRestApi.GetAllActiveHub();
            if (res.status === 200 && res.data.status === 200 && Array.isArray(res.data.data)) {
                setHubOptions(
                    res.data.data.map(h => ({ label: h.hubName, value: h.hubId }))
                );
            }
        } catch (e) {
            console.error('Hub load error:', e);
        }
    };

    // ─── Payload builder ──────────────────────────────────────────────────────
    const buildPayload = (page, size) => ({
        fromDate: fromDate ? `${fromDate}T00:00:00.000Z` : null,
        toDate: toDate ? `${toDate}T23:59:59.000Z` : null,
        orderNo: orderNo.trim() || null,
        selectedHubId: hubId || null,
        page,
        size,
    });

    // ─── Fetch report ─────────────────────────────────────────────────────────
    const fetchReport = async (page, size) => {
        try {
            setLoading(true);
            setNoRecord(false);
            setReport(null);
            setExportReport(null);

            const response = await PndRestApi.GenerateOrderFailedReport(buildPayload(page, size));
            if (response.status === 200 && response.data.status === 200) {
                const data = response.data.data;
                const content = data?.content ?? data;
                setReport(content);
                setTotalElements(data?.totalElements ?? (Array.isArray(content) ? content.length : 0));
                fetchExportData(page, size);
            } else if (response?.data?.status === 204) {
                setNoRecord(true);
            } else {
                showNotification('theme-light', 'ltr', 'Error', response.data?.message || ERROR_MESSAGES.tryCatchError);
            }
        } catch (error) {
            console.error('OrderFailedReport error:', error);
            showNotification('theme-light', 'ltr', 'Error', ERROR_MESSAGES.tryCatchError);
        } finally {
            setLoading(false);
        }
    };

    const fetchExportData = async (page, size) => {
        try {
            const res = await PndRestApi.GenerateOrderFailedReport({ ...buildPayload(page, size), page: 0, size: 5000 });
            if (res.status === 200 && res.data.status === 200) {
                const data = res.data.data;
                setExportReport(data?.content ?? data);
            }
        } catch (e) {
            console.error('Export fetch error:', e);
        }
    };

    const handleSearch = () => { setCurrentPage(0); fetchReport(0, pageSize); };

    const clearFilters = () => {
        setFromDate('');
        setToDate('');
        setOrderNo('');
        setHubId('');
        setCurrentPage(0);
        setReport(null);
        setNoRecord(false);
        setTotalElements(0);
    };

    return (
        <Container fluid>
            {/* ── Header Banner ── */}
            <div style={{
                background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
                borderRadius: 12, padding: '14px 20px', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: '0 4px 18px rgba(220,38,38,0.3)',
            }}>
                <span style={{ fontSize: '1.8rem' }}>❌</span>
                <div>
                    <h5 style={{ margin: 0, color: '#fff', fontWeight: 800 }}>Order Failed Report</h5>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                        View and export failed orders with root cause details
                    </p>
                </div>
            </div>

            {/* ── Filter Card ── */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
                <CardHeader className="border-0 py-3" style={{
                    background: 'linear-gradient(135deg, #fff5f5 0%, #fff 100%)',
                    borderBottom: '1px solid #fecaca',
                    borderTopLeftRadius: 12, borderTopRightRadius: 12,
                }}>
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '1rem' }}>🔍</span>
                        <h6 className="mb-0 fw-bold" style={{ color: '#dc2626' }}>Search Filters</h6>
                    </div>
                </CardHeader>
                <CardBody>
                    <Row className="g-3 align-items-end">
                        {/* From Date */}
                        <Col md={2}>
                            <label className="form-label small fw-bold text-muted mb-1">From Date</label>
                            <Input
                                type="date"
                                value={fromDate}
                                onChange={e => setFromDate(e.target.value)}
                                style={inputStyle}
                            />
                        </Col>
                        {/* To Date */}
                        <Col md={2}>
                            <label className="form-label small fw-bold text-muted mb-1">To Date</label>
                            <Input
                                type="date"
                                value={toDate}
                                onChange={e => setToDate(e.target.value)}
                                style={inputStyle}
                            />
                        </Col>
                        {/* Hub */}
                        <Col md={3}>
                            <label className="form-label small fw-bold text-muted mb-1">Hub</label>
                            <FormControl fullWidth size="small">
                                <Select
                                    displayEmpty
                                    value={hubId}
                                    onChange={e => setHubId(e.target.value)}
                                    input={<OutlinedInput sx={{ fontSize: '0.85rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#fca5a5' } }} />}
                                    renderValue={(val) => {
                                        if (!val) return <span style={{ color: '#aaa' }}>All Hubs</span>;
                                        const h = hubOptions.find(o => o.value === val);
                                        return h ? h.label : val;
                                    }}
                                >
                                    <MenuItem value=""><em>All Hubs</em></MenuItem>
                                    {hubOptions.map(h => (
                                        <MenuItem key={h.value} value={h.value} sx={{ fontSize: '0.85rem' }}>
                                            {h.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Col>
                        {/* Order No */}
                        <Col md={3}>
                            <label className="form-label small fw-bold text-muted mb-1">
                                Order No. <span style={{ fontWeight: 400 }}>(optional)</span>
                            </label>
                            <Input
                                type="text"
                                value={orderNo}
                                onChange={e => setOrderNo(e.target.value)}
                                placeholder="Filter by order number…"
                                style={inputStyle}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            />
                        </Col>
                        {/* Buttons */}
                        <Col md={2} className="d-flex gap-2 align-items-end">
                            <Tooltip title="Reset Filters" arrow>
                                <IconButton
                                    onClick={clearFilters}
                                    sx={{ background: '#f8f9fa', border: '1px solid #dee2e6', '&:hover': { background: '#fee2e2' } }}
                                >
                                    <RestartAltIcon sx={{ color: '#dc2626' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Search" arrow>
                                <IconButton
                                    onClick={handleSearch}
                                    sx={{ background: '#dc2626', color: '#fff', '&:hover': { background: '#b91c1c' } }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </Tooltip>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* ── Progress ── */}
            {loading && <LinearProgress color="error" sx={{ mb: 2, borderRadius: 4 }} />}

            {/* ── Report Table ── */}
            {isReport && (
                <Card className="border-0 shadow-sm" style={{ borderRadius: 12, border: '1px solid #fecaca' }}>
                    <CardBody className="p-0">
                        <OrderFailedReport
                            isReport={isReport}
                            exportReport={exportReport}
                            page={currentPage}
                            rowsPerPage={pageSize}
                            totalElements={totalElements}
                            onPageChange={(p) => { setCurrentPage(p); fetchReport(p, pageSize); }}
                            onRowsPerPageChange={(s) => { setPageSize(s); setCurrentPage(0); fetchReport(0, s); }}
                        />
                    </CardBody>
                </Card>
            )}

            {/* ── No record ── */}
            {isNoRecord && !loading && (
                <SnackbarContent
                    message="✅ No failed orders found for the selected filters."
                    sx={{ mt: 2, background: '#dcfce7', color: '#166534', fontWeight: 600 }}
                />
            )}
        </Container>
    );
};

export default OrderFailedReportIndex;
